import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import React, { useMemo } from "react";
import { ChatListItem } from "@web-src/modules/chats/components/ChatListItem";
import { ChatListItemLastMessage } from "@web-src/modules/chats/components/ChatListItemLastMessage";
import { tokenizeMessageBody } from "@web-src/modules/chats/utils";
import { whatsappMessageToAttachmentPayload } from "@web-src/modules/client-support/utils/whatsappMessageToAttachmentPayload";
import { CHAT_MESSAGE_PAYLOAD_VERSION } from "@jugl-web/utils/consts";
import { isWhatsappPayloadSupported } from "../../../../utils/isWhatsappPayloadSupported";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { isConversationContactBlocked } from "../../../../utils/isConversationContactBlocked";
import { CRMAgentInfo } from "../CRMAgentInfo";

export const WhatsappConversationTile: React.FC<{
  conversation: WhatsappConversationModel;
  isDraftUnavailable: boolean;
  searchQuery?: string;
}> = ({ conversation, searchQuery, isDraftUnavailable }) => {
  const { selectedConversation, selectConversation } =
    useClientSupportPageContext();

  const isContactBlocked = isConversationContactBlocked(conversation);

  const attachment = useMemo(
    () => whatsappMessageToAttachmentPayload(conversation.payload) || undefined,
    [conversation]
  );

  const lastMessageBody = useMemo(() => {
    if ("body" in conversation.payload) {
      return conversation.payload.body;
    }

    if ("text" in conversation.payload) {
      return (conversation.payload as unknown as { text: { body: string } })
        .text.body;
    }

    return attachment?.name || "";
  }, [conversation, attachment]);

  const isUnsupported = useMemo(
    () => !isWhatsappPayloadSupported(conversation.payload.type),
    [conversation.payload.type]
  );

  return (
    <ChatListItem
      variant="inbox"
      chatId={conversation.conv_id}
      onClick={() => selectConversation(conversation)}
      isSelected={selectedConversation?.conv_id === conversation.conv_id}
      title={conversation.contact_info?.name || conversation.name}
      timestamp={conversation.payload.timestamp}
      lastMessageContent={
        <ChatListItemLastMessage
          payloadVersion={CHAT_MESSAGE_PAYLOAD_VERSION}
          tokenizedMessage={tokenizeMessageBody(lastMessageBody)}
          attachments={attachment && [attachment]}
          isUnsupported={isUnsupported}
        />
      }
      isDraftUnavailable={isDraftUnavailable || isContactBlocked}
      searchQuery={searchQuery}
      additionalContent={<CRMAgentInfo conversation={conversation} />}
      showUnreadIndicator={conversation.type === "incoming"}
      hasChatAccess
    />
  );
};
